import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoRefreshOutline,
  IoChevronForwardOutline,
  IoChevronDownOutline,
  IoChevronUpOutline,
  IoChatbubbleEllipsesOutline,
  IoCalendarOutline,
  IoCheckmarkCircleOutline,
  IoWalletOutline,
  IoTrendingUpOutline,
} from 'react-icons/io5';
import { collection, query, where, getDocs, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';
import { getPartnerDashboardStats } from '../../../services/partnerDashboardService';
import { getComplaintsByPartner } from '../../../services/complaintService';
import { openMail } from '../../../utils/openMail';
import { openChannelTalkWorkflow } from '../../../services/channelTalkService';
import Spinner from '../../../components/common/Spinner';
import { IEUM_BUSINESS_INFO } from '../../../config/ieumConfig';

const EMPTY_STATS = {
  inProgressCount: 0,
  monthlyCompletedCount: 0,
  receivableTotal: 0,
  receivableCount: 0,
  conversionRate: 0,
  pipelineCounts: {
    pre_consult: 0, in_progress: 0, consulting: 0,
    visiting: 0, reserved: 0, absent: 0,
    completed: 0, cancelled: 0, pending: 0,
  },
  assignedConvRate: 0,
  purchasedConvRate: 0,
  receivableList: [],
};

const PROGRESS_CARDS = [
  { key: 'consulting', label: '상담 중' },
  { key: 'absent', label: '부재 중' },
  { key: 'visiting', label: '방문상담 예약' },
  { key: 'reserved', label: '계약 완료' },
];

const CLOSED_CARDS = [
  { key: 'completed', label: '전체 완료' },
  { key: 'cancelled', label: '취소' },
];

const fmt = (n) => `₩${(n || 0).toLocaleString()}`;

const tsOf = (x) =>
  x?.seconds ? x.seconds * 1000
  : x?.toDate ? x.toDate().getTime()
  : typeof x === 'string' ? new Date(x).getTime()
  : 0;

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState(EMPTY_STATS);
  const [complaints, setComplaints] = useState([]);
  const [pendingBills, setPendingBills] = useState([]);
  const [oldReceivables, setOldReceivables] = useState([]);
  const [mailToast, setMailToast] = useState(false);
  const [bizOpen, setBizOpen] = useState(false);

  const companyName = userData?.business_name || userData?.businessName || userData?.name || '제휴업체';
  const email = userData?.email || '';

  const loadStats = useCallback(async () => {
    if (!userData?.uid) return;
    setLoading(true);
    try {
      const [stats, comps, billsSnap, recvSnap] = await Promise.all([
        getPartnerDashboardStats(userData.uid),
        getComplaintsByPartner(userData.uid),
        getDocs(query(
          collection(db, 'partner_payment_requests'),
          where('partner_id', '==', userData.uid),
          where('status', '==', 'pending'),
        )).catch(() => null),
        getDocs(query(
          collection(db, 'partner_receivables'),
          where('partner_id', '==', userData.uid),
          where('status', '==', 'pending'),
        )).catch(() => null),
      ]);
      setD(stats);
      setComplaints(comps || []);
      setPendingBills(billsSnap ? billsSnap.docs.map((x) => ({ id: x.id, ...x.data() })) : []);
      setOldReceivables(recvSnap ? recvSnap.docs.map((x) => ({ id: x.id, ...x.data() })) : []);
    } catch (err) {
      console.error('dashboard stats failed', err);
    } finally {
      setLoading(false);
    }
  }, [userData?.uid]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleChannelOpen = () => {
    openChannelTalkWorkflow('partner');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadStats();
    setRefreshing(false);
  };

  const handleMailClick = async (e) => {
    e.preventDefault();
    const { copied } = await openMail('biz@ieum.in');
    if (copied) {
      setMailToast(true);
      setTimeout(() => setMailToast(false), 2500);
    }
  };

  // 조건부 배너: pending 결제요청 최신 1건
  const latestPendingBill = useMemo(() => {
    if (pendingBills.length === 0) return null;
    return [...pendingBills].sort((a, b) => tsOf(b.created_at || b.createdAt) - tsOf(a.created_at || a.createdAt))[0];
  }, [pendingBills]);

  // 24h 초과 미결제 경고
  const hasOldUnpaid = useMemo(() => {
    const now = Date.now();
    const limit = 24 * 60 * 60 * 1000;
    const oldBill = pendingBills.some((b) => now - tsOf(b.created_at || b.createdAt) > limit);
    const oldRecv = oldReceivables.some((r) => now - tsOf(r.created_at || r.createdAt) > limit);
    return oldBill || oldRecv;
  }, [pendingBills, oldReceivables]);

  const pendingComplaints = complaints.filter(
    (c) => !['resolved', 'completed', 'closed', 'done'].includes(c.status),
  );

  const handlePayBill = (bill) => {
    navigate(`/partner/payment/process/${bill.id}`);
  };

  const handleCancelBill = async (bill) => {
    if (!window.confirm('이 결제 요청을 취소하시겠습니까?')) return;
    try {
      await updateDoc(doc(db, 'partner_payment_requests', bill.id), {
        status: 'cancelled',
        cancelled_at: serverTimestamp(),
        cancelled_by: userData?.uid || '',
      });
      await loadStats();
    } catch (err) {
      alert(err?.message || '취소 실패');
    }
  };

  // 당월 매칭 = 내가 받은 배정(pre_consult 이후 전부) 중 이번달 기준 count — pipelineCounts로 근사
  const monthMatched = Object.values(d.pipelineCounts || {}).reduce((a, b) => a + b, 0);
  const monthContracted = (d.pipelineCounts?.reserved || 0) + (d.pipelineCounts?.completed || 0);

  if (loading) return <Spinner fullPage={false} />;

  return (
    <Wrap>
      <HeaderRow>
        <HeaderLeft>
          <HeaderTitle>{companyName}</HeaderTitle>
          <HeaderEmail>{email}</HeaderEmail>
        </HeaderLeft>
        <RefreshBtn type="button" onClick={handleRefresh} disabled={refreshing}>
          <IoRefreshOutline size={14} />
          새로고침
        </RefreshBtn>
      </HeaderRow>

      {/* 조건부 결제요청 배너 */}
      {latestPendingBill && (
        <PayBanner>
          <PayBannerTop>
            <PayBannerTitle>결제 요청이 있습니다</PayBannerTitle>
            {pendingBills.length > 1 && (
              <PayBannerMore>외 {pendingBills.length - 1}건</PayBannerMore>
            )}
          </PayBannerTop>
          <PayBannerRow>
            <PayBannerInfo>
              <PayBannerLabel>{latestPendingBill.label || latestPendingBill.memo || '본사 청구'}</PayBannerLabel>
              <PayBannerAmount>{fmt(latestPendingBill.amount)}</PayBannerAmount>
            </PayBannerInfo>
            <PayBannerBtns>
              <PayBtn type="button" onClick={() => handlePayBill(latestPendingBill)}>결제하기</PayBtn>
              <CancelBtn type="button" onClick={() => handleCancelBill(latestPendingBill)}>취소하기</CancelBtn>
            </PayBannerBtns>
          </PayBannerRow>
        </PayBanner>
      )}

      {/* 24h 초과 경고 */}
      {hasOldUnpaid && (
        <WarnBox>
          하루 이상 결제되지 않은 미수건이 있어 마켓 이용이 제한될 수 있습니다.
          빠른 결제 부탁드립니다.
        </WarnBox>
      )}

      {/* KPI 4 카드 */}
      <KpiGrid>
        <KpiCard>
          <KpiIcon $bg="#EEF2FF" $c="#4A6CF7"><IoCalendarOutline size={18} /></KpiIcon>
          <KpiLabel>당월 매칭 수량</KpiLabel>
          <KpiValue>{monthMatched}건</KpiValue>
          <KpiDesc>DB 구매 + 배정</KpiDesc>
        </KpiCard>
        <KpiCard>
          <KpiIcon $bg="#ECFDF5" $c="#22C55E"><IoCheckmarkCircleOutline size={18} /></KpiIcon>
          <KpiLabel>당월 계약완료</KpiLabel>
          <KpiValue $c="#22C55E">{monthContracted}건</KpiValue>
          <KpiDesc>이번 달 기준</KpiDesc>
        </KpiCard>
        <KpiCard>
          <KpiIcon $bg="#FFF7ED" $c="#F59E0B"><IoWalletOutline size={18} /></KpiIcon>
          {d.receivableCount > 0 && (
            <KpiTopAction type="button" onClick={() => navigate('/partner/settlement')}>
              결제하기
            </KpiTopAction>
          )}
          <KpiLabel>미수금 합계</KpiLabel>
          <KpiValue $c="#F59E0B">{fmt(d.receivableTotal)}</KpiValue>
          <KpiDesc>결제 대기 {d.receivableCount || 0}건</KpiDesc>
        </KpiCard>
        <KpiCard title="배정/구매 DB 중 예약·완료 비율">
          <KpiIcon $bg="#F5F3FF" $c="#8B5CF6"><IoTrendingUpOutline size={18} /></KpiIcon>
          <KpiLabel>계약완료 전환율</KpiLabel>
          <KpiValue>{(d.conversionRate || 0).toFixed(1)}%</KpiValue>
          <KpiDesc>배정 → 완료</KpiDesc>
        </KpiCard>
      </KpiGrid>

      {/* 서비스 진행 현황 + 완료 / 종결 (한 카드) */}
      <SectionBox>
        <SectionHeader>
          <SectionTitle>서비스 진행 현황</SectionTitle>
          <ChevronBtn onClick={() => navigate('/partner/db?tab=mine')}>
            <IoChevronForwardOutline size={18} />
          </ChevronBtn>
        </SectionHeader>
        <ProgressGrid>
          {PROGRESS_CARDS.map((p) => (
            <ProgressCard
              key={p.key}
              type="button"
              onClick={() => navigate(`/partner/db?tab=mine&status=${p.key}`)}
            >
              <ProgressLabel>{p.label}</ProgressLabel>
              <ProgressCount>{d.pipelineCounts[p.key] || 0}</ProgressCount>
            </ProgressCard>
          ))}
        </ProgressGrid>

        <SubSectionHeader>
          <SectionTitle>완료 / 종결</SectionTitle>
        </SubSectionHeader>
        <ClosedGrid>
          {CLOSED_CARDS.map((p) => (
            <ProgressCard
              key={p.key}
              type="button"
              onClick={() => navigate(`/partner/db?tab=mine&status=${p.key}`)}
            >
              <ProgressLabel>{p.label}</ProgressLabel>
              <ProgressCount>{d.pipelineCounts[p.key] || 0}</ProgressCount>
            </ProgressCard>
          ))}
        </ClosedGrid>
      </SectionBox>

      {/* 미수금 (읽기 전용) */}
      <SectionBox>
        <SectionHeader>
          <SectionTitle>미수금</SectionTitle>
          <SectionMeta $warn={d.receivableCount > 0}>
            미결제 {d.receivableCount}건
          </SectionMeta>
        </SectionHeader>
        <List>
          {d.receivableList.length === 0 ? (
            <EmptyText>미수금이 없습니다.</EmptyText>
          ) : (
            d.receivableList.slice(0, 3).map((r) => (
              <ListItem key={r.id}>
                <ListMain>미수금</ListMain>
                <ListAmount>{fmt(r.amount)}</ListAmount>
              </ListItem>
            ))
          )}
        </List>
        <SeeAllRow>
          <SeeAllBtn onClick={() => navigate('/partner/settlement/receivable')}>
            전체 보기 →
          </SeeAllBtn>
        </SeeAllRow>
      </SectionBox>

      {/* 민원 확인 */}
      <SectionBox>
        <SectionHeader>
          <SectionTitle>민원 확인</SectionTitle>
          <SectionMeta $warn={pendingComplaints.length > 0}>
            미처리 {pendingComplaints.length}건
          </SectionMeta>
        </SectionHeader>
        <List>
          {pendingComplaints.length === 0 ? (
            <EmptyText>처리 대기 중인 민원이 없습니다.</EmptyText>
          ) : (
            pendingComplaints.slice(0, 3).map((c) => (
              <ListItem key={c.id} onClick={() => navigate('/partner/complaints')} style={{ cursor: 'pointer' }}>
                <ListMain>{c.title || c.summary || c.content || '민원'}</ListMain>
                <ListMeta>{c.customerName || c.date || ''}</ListMeta>
              </ListItem>
            ))
          )}
        </List>
        <SeeAllRow>
          <SeeAllBtn onClick={() => navigate('/partner/complaints')}>
            전체 보기 →
          </SeeAllBtn>
        </SeeAllRow>
      </SectionBox>

      {/* 사업자 정보 아코디언 — 이음플렛폼 공식 사업자 정보 */}
      <SectionBox>
        <AccordionHeader type="button" onClick={() => setBizOpen((v) => !v)}>
          <BizTitleCol>
            <SectionTitle>이음 플렛폼 사업자 정보</SectionTitle>
            <BizTitleSub>(주)이음의 플렛폼 운영사 정보입니다</BizTitleSub>
          </BizTitleCol>
          {bizOpen ? <IoChevronUpOutline size={18} /> : <IoChevronDownOutline size={18} />}
        </AccordionHeader>
        {bizOpen && (
          <InfoGrid>
            <InfoRow><InfoKey>업체명</InfoKey><InfoVal>{IEUM_BUSINESS_INFO.companyName}</InfoVal></InfoRow>
            <InfoRow><InfoKey>대표자</InfoKey><InfoVal>{IEUM_BUSINESS_INFO.representative}</InfoVal></InfoRow>
            <InfoRow><InfoKey>사업자번호</InfoKey><InfoVal>{IEUM_BUSINESS_INFO.businessNumber}</InfoVal></InfoRow>
            <InfoRow><InfoKey>주소</InfoKey><InfoVal>{IEUM_BUSINESS_INFO.address}</InfoVal></InfoRow>
            <InfoRow><InfoKey>고객센터</InfoKey><InfoVal>{IEUM_BUSINESS_INFO.supportEmail}</InfoVal></InfoRow>
            <InfoRow><InfoKey>대표번호</InfoKey><InfoVal>{IEUM_BUSINESS_INFO.supportPhone}</InfoVal></InfoRow>
          </InfoGrid>
        )}
      </SectionBox>

      {/* 약관 링크 */}
      <TermsRow>
        <TermLink href="/terms/service" target="_blank" rel="noreferrer">이용약관</TermLink>
        <TermDivider />
        <TermLink href="/terms/privacy" target="_blank" rel="noreferrer">개인정보처리방침</TermLink>
        <TermDivider />
        <TermLink href="/terms/license" target="_blank" rel="noreferrer">오픈소스 라이선스</TermLink>
      </TermsRow>

      {/* 채팅상담 + 메일 문의 */}
      <SupportRow>
        <ChannelBtn type="button" onClick={handleChannelOpen}>
          <IoChatbubbleEllipsesOutline size={16} />
          채팅상담 (채널톡)
        </ChannelBtn>
        <MailLine>
          메일 문의 :
          <MailAnchor type="button" onClick={handleMailClick}>biz@ieum.in</MailAnchor>
        </MailLine>
      </SupportRow>

      {mailToast && <MailToast>biz@ieum.in 주소가 복사됐어요</MailToast>}
    </Wrap>
  );
};

export default PartnerDashboard;

// ───── 스타일 ─────
const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (min-width: 1024px) {
    max-width: 720px;
    margin: 0 auto;
    width: 100%;
    gap: 18px;
  }
`;

const HeaderRow = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
`;
const HeaderLeft = styled.div` min-width: 0; `;
const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  line-height: 1.3;
  @media (min-width: 1024px) { font-size: 22px; }
`;
const HeaderEmail = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 4px;
`;
const RefreshBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 6px 12px;
  background: #fff;
  color: ${({ theme }) => theme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  &:disabled { opacity: 0.6; }
`;

// 결제 요청 배너 (primary 강조)
const PayBanner = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const PayBannerTop = styled.div`
  display: flex; align-items: center; justify-content: space-between;
`;
const PayBannerTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
`;
const PayBannerMore = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.8);
`;
const PayBannerRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
`;
const PayBannerInfo = styled.div` min-width: 0; `;
const PayBannerLabel = styled.div`
  font-size: 13px;
  color: rgba(255,255,255,0.9);
  margin-bottom: 2px;
`;
const PayBannerAmount = styled.div`
  font-size: 20px;
  font-weight: 700;
`;
const PayBannerBtns = styled.div`
  display: flex; gap: 8px; flex-shrink: 0;
`;
const PayBtn = styled.button`
  padding: 8px 16px;
  background: #fff;
  color: ${({ theme }) => theme.colors.primary};
  border: 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover { opacity: 0.92; }
`;
const CancelBtn = styled.button`
  padding: 8px 14px;
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255,255,255,0.5);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: rgba(255,255,255,0.1); }
`;

// 경고 박스 (연노랑 — 안내 박스 규칙 허용)
const WarnBox = styled.div`
  padding: 12px 14px;
  background: #FEF3C7;
  border: 1px solid #FCD34D;
  border-radius: 8px;
  font-size: 12px;
  color: #92400E;
  line-height: 1.5;
`;

// KPI
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;
const KpiCard = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 14px 16px;
`;
const KpiLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;
const KpiValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${({ $c }) => $c || '#1F2937'};
  line-height: 1.1;
`;
const KpiIcon = styled.div`
  width: 30px; height: 30px;
  border-radius: 8px;
  background: ${({ $bg }) => $bg || '#F3F4F6'};
  color: ${({ $c }) => $c || '#6B7280'};
  display: inline-flex; align-items: center; justify-content: center;
  margin-bottom: 8px;
`;
const KpiDesc = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 4px;
`;
const KpiAction = styled.button`
  margin-top: 10px;
  padding: 8px 12px;
  background: #F59E0B;
  color: #fff;
  border: 0;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #D97706; }
`;
const KpiTopAction = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 6px 10px;
  background: #F59E0B;
  color: #fff;
  border: 0;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #D97706; }
`;

// 공통 섹션
const SectionBox = styled.section`
  background: #F7F8FB;
  border: 1px solid #E8ECF2;
  border-radius: 12px;
  padding: 16px;
`;
const SectionHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px;
`;
const SubSectionHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 20px;
  margin-bottom: 12px;
`;
const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;
const BizTitleCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
`;
const BizTitleSub = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const SectionMeta = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $warn }) => ($warn ? '#EF4444' : '#6B7280')};
`;
const ChevronBtn = styled.button`
  background: none; border: 0; padding: 0; cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
`;

// 진행 현황 / 종결
const ProgressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  @media (min-width: 640px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;
const ClosedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;
const ProgressCard = styled.button`
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 14px 12px;
  background: #FFFFFF;
  border: 1px solid #E8ECF2;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  &:hover { background: #F9FAFB; }
`;
const ProgressLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const ProgressCount = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

// 리스트
const List = styled.div`
  display: flex; flex-direction: column;
`;
const ListItem = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 10px;
  padding: 10px 0;
  font-size: 13px;
  &:not(:last-child) { border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight}; }
`;
const ListMain = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;
const ListAmount = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;
const ListMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
`;
const EmptyText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  padding: 16px 0;
`;
const SeeAllRow = styled.div`
  display: flex; justify-content: flex-end;
  padding-top: 10px;
  margin-top: 6px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;
const SeeAllBtn = styled.button`
  background: none; border: 0; padding: 0;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

// 아코디언
const AccordionHeader = styled.button`
  width: 100%;
  display: flex; align-items: center; justify-content: space-between;
  background: none; border: 0; padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const InfoGrid = styled.div`
  display: flex; flex-direction: column;
  margin-top: 14px;
`;
const InfoRow = styled.div`
  display: flex; justify-content: space-between; gap: 12px;
  padding: 8px 0;
  font-size: 13px;
  &:not(:last-of-type) { border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight}; }
`;
const InfoKey = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-shrink: 0;
`;
const InfoVal = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
  text-align: right;
  word-break: break-all;
`;
const EditBtn = styled.button`
  align-self: flex-end;
  margin-top: 10px;
  padding: 6px 14px;
  background: #fff;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
`;

// 약관
const TermsRow = styled.div`
  display: flex; justify-content: center; align-items: center;
  gap: 10px; flex-wrap: wrap;
  padding: 12px 0;
`;
const TermLink = styled.a`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  &:hover { color: ${({ theme }) => theme.colors.text}; text-decoration: underline; }
`;
const TermDivider = styled.span`
  width: 1px;
  height: 10px;
  background: ${({ theme }) => theme.colors.border};
`;

// 채팅
const SupportRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 0 4px;
  flex-wrap: nowrap;
  min-width: 0;
`;

const ChannelBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 12px;
  background: #fff;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover { background: #F9FAFB; }
`;

const MailLine = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  min-width: 0;
  flex-shrink: 1;
  overflow: hidden;
`;

const MailAnchor = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
`;

const ChatBtn = styled.button`
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: 0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.92; }
`;

const MailToast = styled.div`
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(17, 24, 39, 0.92);
  color: #fff;
  padding: 12px 18px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  z-index: 9999;
  box-shadow: 0 4px 16px rgba(0,0,0,0.18);
`;
